import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, Users, Star, Check, ChevronDown, ChevronUp, Heart, MessageSquare, ShoppingCart, Loader2, Eye } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { courses as fallbackCourses, getCurriculum as fallbackCurriculum } from "../data/mockData";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function CourseDetail() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openStage, setOpenStage] = useState(0);
    const [enrolling, setEnrolling] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const c = await api.get(`/courses/${courseId}`);
                if (active) setCourse(c);
            } catch {
                // fallback to mock
                const c = fallbackCourses.find((x) => x.id === courseId) || fallbackCourses[0];
                if (active) setCourse({ ...c, curriculum: fallbackCurriculum(courseId) });
            } finally { if (active) setLoading(false); }
        })();
        return () => { active = false; };
    }, [courseId]);

    if (loading || !course) {
        return <AppLayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div></AppLayout>;
    }

    const curriculum = course.curriculum || [];

    const startCheckout = async () => {
        if (!user) { toast.error("Please sign in to enroll"); navigate("/login"); return; }
        if (course.enrolled) { navigate(`/learn/${course.id}`); return; }
        setEnrolling(true);
        try {
            const r = await api.post("/checkout/session", {
                course_id: course.id,
                origin_url: window.location.origin,
            });
            window.location.href = r.url;
        } catch (e) {
            console.error(e);
            toast.error("Could not start checkout. Try again.");
            setEnrolling(false);
        }
    };

    const addWishlist = async () => {
        if (!user) { toast.error("Sign in to use your wishlist"); return; }
        try { await api.post("/wishlist", { course_id: course.id }); toast.success("Added to wishlist"); }
        catch { toast.error("Couldn't add"); }
    };

    const addCart = async () => {
        if (!user) { toast.error("Sign in to use your cart"); return; }
        try { await api.post("/cart", { course_id: course.id }); toast.success("Added to cart"); }
        catch { toast.error("Couldn't add"); }
    };

    return (
        <AppLayout searchPlaceholder="Search for courses...">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-8">
                        <span className="px-3 py-1 rounded-full bg-grape/15 text-grape text-xs font-semibold border border-grape/30">{course.category}</span>
                        <h1 data-testid="course-title" className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-tight mt-5">{course.title}</h1>
                        <p className="text-gray-400 mt-5 leading-relaxed max-w-2xl">{course.description}</p>
                        <div className="mt-7 flex flex-wrap items-center gap-6 text-sm">
                            <span className="flex items-center gap-2 text-lime"><Clock className="w-4 h-4" /> {course.duration_hours || course.durationHours} Hours</span>
                            <span className="flex items-center gap-2 text-lime"><Users className="w-4 h-4" /> {(course.students || 0).toLocaleString()} Students</span>
                            <span className="flex items-center gap-2">
                                {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.floor(course.rating || 0) ? "text-lime" : "text-gray-600"}`} fill="currentColor" />)}
                                <span className="text-white font-semibold">{course.rating}</span>
                                <span className="text-gray-500">({(course.ratings_count || course.ratingsCount || 0).toLocaleString()} ratings)</span>
                            </span>
                        </div>
                        <div className="mt-6">
                            <Link to={`/community/${course.id}`} data-testid="community-link" className="inline-flex items-center gap-2 text-sm text-grape hover:underline font-semibold">
                                <MessageSquare className="w-4 h-4" /> Visit course community
                            </Link>
                        </div>
                    </div>

                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-8">
                        <h2 className="font-display font-bold text-2xl mb-6">Course Curriculum</h2>
                        <div className="space-y-3">
                            {curriculum.map((stage, idx) => {
                                const isOpen = openStage === idx;
                                return (
                                    <div key={idx} className="rounded-2xl bg-ink-800 border border-white/5 overflow-hidden">
                                        <button data-testid={`stage-${idx}`} onClick={() => setOpenStage(isOpen ? -1 : idx)} className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-ink-700 transition-colors">
                                            <span className="font-semibold">{stage.stage}</span>
                                            <span className="flex items-center gap-3 text-xs text-gray-400">
                                                <span>{stage.lessons.length} lessons</span>
                                                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </span>
                                        </button>
                                        {isOpen && (
                                            <div className="px-5 py-3 border-t border-white/5">
                                                {stage.lessons.map((l) => (
                                                    <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
                                                        <span className="flex items-center gap-3">
                                                            <span className="w-7 h-7 rounded-full bg-ink-600 text-gray-500 border border-white/10 flex items-center justify-center text-xs">▷</span>
                                                            <span>{l.title}</span>
                                                        </span>
                                                        <span className="text-gray-500">{l.duration}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-ink-700 rounded-3xl border border-white/5 overflow-hidden sticky top-24">
                        <div className="aspect-video">
                            <img src={course.cover} alt={course.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-6">
                            <div className="flex items-baseline gap-3">
                                <span className="font-display font-black text-4xl">£{course.price}</span>
                                <span className="text-gray-500 line-through">£{course.original_price || course.originalPrice}</span>
                            </div>
                            <p className="mt-1 text-sm text-red-400">{course.sale_percent || course.salePercent}% off. Sale ends in 3 days!</p>

                            {user?.role === 'admin' ? (
                                <button onClick={() => navigate(`/learn/${course.id}`)} className="mt-5 w-full px-6 py-3.5 rounded-full bg-grape text-white font-bold hover:bg-grape-hover transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2">
                                    <Eye className="w-4 h-4" /> Preview Course (Admin)
                                </button>
                            ) : (
                                <button onClick={startCheckout} disabled={enrolling} data-testid="enroll-now-btn" className="mt-5 w-full px-6 py-3.5 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                                    {enrolling ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : (course.enrolled ? "Continue learning" : "Enroll Now")}
                                </button>
                            )}
                            <div className="mt-2 grid grid-cols-2 gap-2">
                                <button onClick={addWishlist} data-testid="wishlist-btn" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/10 text-white text-sm font-semibold hover:bg-white/5 transition-colors">
                                    <Heart className="w-4 h-4" /> Wishlist
                                </button>
                                <button onClick={addCart} data-testid="cart-btn" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/10 text-white text-sm font-semibold hover:bg-white/5 transition-colors">
                                    <ShoppingCart className="w-4 h-4" /> Cart
                                </button>
                            </div>
                            <p className="text-center text-xs text-gray-500 mt-4">30-Day Money-Back Guarantee</p>
                        </div>
                    </div>

                    <div className="bg-ink-700 rounded-3xl border border-white/5 p-6">
                        <h3 className="font-display font-bold text-lg mb-4">About the Instructor</h3>
                        <div className="flex items-start gap-3">
                            <img src={course.instructor_avatar || course.instructor?.avatar} alt={course.instructor_name || course.instructor?.name} className="w-14 h-14 rounded-full object-cover" />
                            <div>
                                <div className="font-semibold">{course.instructor_name || course.instructor?.name}</div>
                                <div className="text-sm text-grape">{course.instructor_title || course.instructor?.title}</div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-400 mt-4 leading-relaxed">
                            Nige is BMG&apos;s lead instructor across the entire curriculum. 25+ years operating across military, government and corporate security disciplines — and the calm voice in the room when it matters.
                        </p>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
